/**
 * MongoDB implementation of PersonAuth Repository
 * 
 * Clean infrastructure layer implementation
 * No domain logic here!
 */
import { MongoClient, Db, Collection } from 'mongodb';
import { PersonAuth } from '../../bounded-contexts/authentication/domain/aggregates/PersonAuth';
import { IPersonAuthRepository } from '../../bounded-contexts/authentication/domain/repositories/IPersonAuthRepository';
import { v4 as uuidv4 } from 'uuid';

export class MongoPersonAuthRepository implements IPersonAuthRepository {
  private db: Db;
  private collection: Collection;
  private client: MongoClient;

  constructor(
    private readonly mongoUrl: string,
    private readonly dbName: string
  ) {}

  private async ensureConnection(): Promise<void> {
    if (!this.client) {
      this.client = new MongoClient(this.mongoUrl);
      await this.client.connect();
      this.db = this.client.db(this.dbName);
      this.collection = this.db.collection('person_auth');
      
      // Create indexes
      await this.collection.createIndex({ email: 1 }, { unique: true });
      await this.collection.createIndex({ personId: 1 });
    }
  }

  async save(personAuth: PersonAuth): Promise<void> {
    await this.ensureConnection();
    const snapshot = personAuth.toSnapshot();
    
    await this.collection.replaceOne(
      { _id: snapshot.id },
      { _id: snapshot.id, ...snapshot },
      { upsert: true }
    );
  }

  async findById(id: string): Promise<PersonAuth | null> {
    await this.ensureConnection();
    const doc = await this.collection.findOne({ _id: id });
    
    if (!doc) return null;
    
    return PersonAuth.fromSnapshot(doc);
  }

  async findByEmail(email: string): Promise<PersonAuth | null> {
    await this.ensureConnection();
    const doc = await this.collection.findOne({ email: email.toLowerCase() });
    
    if (!doc) return null;
    
    return PersonAuth.fromSnapshot(doc);
  }

  async findByPersonId(personId: string): Promise<PersonAuth | null> {
    await this.ensureConnection();
    const doc = await this.collection.findOne({ personId });
    
    if (!doc) return null;
    
    return PersonAuth.fromSnapshot(doc);
  }

  async emailExists(email: string): Promise<boolean> {
    await this.ensureConnection();
    const count = await this.collection.countDocuments({ 
      email: email.toLowerCase() 
    });
    
    return count > 0;
  }

  async remove(id: string): Promise<void> {
    await this.ensureConnection();
    await this.collection.deleteOne({ _id: id });
  }

  nextIdentity(): string {
    return uuidv4();
  }

  async disconnect(): Promise<void> {
    if (this.client) {
      await this.client.close();
    }
  }
}